import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { downloadFile } from "../lib/download";
import { ENTRY_TYPES, CURRENCIES, STATUS_OPTIONS } from "../lib/constants";
import { PlusCircle, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

function fmt(n) {
  return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Register() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [entryType, setEntryType] = useState("");
  const [currency, setCurrency] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (entryType) params.entry_type = entryType;
      if (currency) params.currency = currency;
      if (status) params.status = status;
      const { data } = await api.get("/transactions", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [entryType, currency, status]);

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (entryType) params.set("entry_type", entryType);
      if (currency) params.set("currency", currency);
      if (status) params.set("status", status);
      const qs = params.toString();
      await downloadFile(`/export/transactions.xlsx${qs ? `?${qs}` : ""}`, "transactions.xlsx");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totals = useMemo(() => {
    const t = {};
    rows.forEach((r) => {
      const cur = r.currency || "USD";
      t[cur] = t[cur] || { in: 0, out: 0 };
      if (r.money_direction === "Money In") t[cur].in += Number(r.amount || 0);
      if (r.money_direction === "Money Out") t[cur].out += Number(r.amount || 0);
    });
    return t;
  }, [rows]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Transactions</h1>
          <p className="text-slate-500 mt-1 text-sm">{rows.length} entries · search, filter, review</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportExcel}
            data-testid="register-export-btn"
            className="inline-flex items-center gap-2 border-2 border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold rounded-xl px-4 h-12 transition-colors"
          >
            <Download size={16} /> Export Excel
          </button>
          <button
            onClick={() => nav("/entry")}
            data-testid="register-new-entry-btn"
            className="inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold rounded-xl px-5 h-12 transition-colors"
          >
            <PlusCircle size={18} /> New Entry
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="col-span-2 md:col-span-2 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="filter-search"
            placeholder="Search party, PI, purpose, reference…"
            className="w-full h-12 pl-10 pr-4 border border-slate-300 rounded-xl bg-white text-sm outline-none"
          />
        </div>
        <select value={entryType} onChange={(e) => setEntryType(e.target.value)} data-testid="filter-entry-type"
          className="h-12 px-3 border border-slate-300 rounded-xl bg-white text-sm outline-none">
          <option value="">All entry types</option>
          {ENTRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="filter-currency"
          className="h-12 px-3 border border-slate-300 rounded-xl bg-white text-sm outline-none">
          <option value="">All currencies</option>
          {CURRENCIES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Currency totals bar */}
      {Object.keys(totals).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(totals).map(([cur, v]) => (
            <div key={cur} className="border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-xs">
              <span className="font-mono font-semibold text-slate-700">{cur}</span>
              <span className="ml-2 text-emerald-600 tabular">+{fmt(v.in)}</span>
              <span className="ml-2 text-red-600 tabular">−{fmt(v.out)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="border border-slate-200 rounded-xl bg-white overflow-hidden overflow-x-auto">
        {loading ? (
          <div className="p-8 text-slate-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-slate-500 text-sm">No entries yet.</div>
            <button
              onClick={() => nav("/entry")}
              className="mt-4 inline-flex items-center gap-2 text-[#0055FF] font-semibold text-sm hover:underline"
            >
              <PlusCircle size={16} /> Create your first entry
            </button>
          </div>
        ) : (
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase bg-slate-50/70">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">PI</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Party</th>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3 text-right">In</th>
                <th className="px-4 py-3 text-right">Out</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody data-testid="tx-table-body">
              {rows.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => nav(`/entry/${t.id}`)}
                  className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer transition-colors"
                  data-testid={`tx-row-${t.id}`}
                >
                  <td className="px-4 py-3 text-sm text-slate-700 tabular">{(t.transaction_date || "").slice(0,10)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${
                      t.money_direction === "Money In" ? "bg-emerald-50 text-emerald-700" :
                      t.money_direction === "Money Out" ? "bg-red-50 text-red-700" :
                      "bg-slate-100 text-slate-700"
                    }`}>{t.entry_type}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {t.pi_number ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); nav(`/pi/${encodeURIComponent(t.pi_number)}`); }}
                        className="text-[#0055FF] font-medium hover:underline"
                        data-testid={`pi-link-${t.id}`}
                      >
                        {t.pi_number}
                      </button>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{t.company || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{t.party_name || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{t.account || "—"}</td>
                  <td className="px-4 py-3 text-sm text-right tabular text-emerald-700 font-semibold">
                    {t.money_direction === "Money In" ? `${t.currency} ${fmt(t.amount)}` : ""}
                  </td>
                  <td className="px-4 py-3 text-sm text-right tabular text-red-700 font-semibold">
                    {t.money_direction === "Money Out" ? `${t.currency} ${fmt(t.amount)}` : ""}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">{t.status || "New"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
